import { QStatus } from '../synchronization.model';
import {
    DAY_PICKER_DAYS,
    ExecutorGroup,
    ExecutorGroupKey,
    QueueExecuterDto,
    QueuePhaseDto,
    QueueRequestDetailDto,
    QueueRequestListItemDto,
    QueueRequestRow,
    QueueTimeWindow
} from './queue-request.model';

/**
 * Note there is no `matchesSearch` here any more. Search is a server filter, because the counts have
 * to honour it — the badges would otherwise ignore the search box, and the whole point of the badges
 * is that they can be trusted at a glance.
 */

/** Pure helpers shared by the browser and its children. No component state, no HTTP. */

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

/**
 * Terminal for polling purposes: nothing more will happen to this kørsel on its own.
 *
 * Only used to decide the poll cadence — the row's group comes from the server, which is the single
 * authority on classification.
 */
export function isTerminal(status: QStatus | null): boolean {
    switch (status) {
        case QStatus.Done:
        case QStatus.DoneWithErrors:
        case QStatus.DoneWithWarnings:
        case QStatus.Failed:
        case QStatus.SystemFailed:
        case QStatus.Cancelled:
            return true;
        default:
            return false;
    }
}

export function progressOf(item: QueueRequestListItemDto): QueueRequestRow['progress'] {
    const total = item.executerTotal;
    return {
        done: item.executerFinished,
        total,
        failed: item.executerFailed,
        percent: total > 0 ? Math.round((item.executerFinished / total) * 100) : 0
    };
}

// ── Chunk grouping ───────────────────────────────────────────────────────────

const EXECUTOR_GROUP_ORDER: readonly { key: ExecutorGroupKey; label: string; variant: string }[] = [
    { key: 'failed', label: 'Fejlede delkørsler', variant: 'danger' },
    { key: 'running', label: 'Kører nu', variant: 'primary' },
    { key: 'waiting', label: 'Venter', variant: 'secondary' },
    { key: 'done', label: 'Færdige', variant: 'success' }
];

/**
 * A delkørsel that has stopped for good, whatever the outcome. Mirrors `StatusService.IsSettled` in
 * Crono-Synchronization: `PostSynchronizationQueued` is still in flight.
 */
export function isExecutorSettled(status: QStatus | null): boolean {
    switch (status) {
        case QStatus.Done:
        case QStatus.DoneWithErrors:
        case QStatus.DoneWithWarnings:
        case QStatus.Warnings:
        case QStatus.Failed:
        case QStatus.SystemFailed:
        case QStatus.Cancelled:
            return true;
        default:
            return false;
    }
}

export function executorGroupOf(status: QStatus | null): ExecutorGroupKey {
    switch (status) {
        case QStatus.Failed:
            return 'failed';
        case QStatus.Executing:
            return 'running';
        case QStatus.Waiting:
            return 'waiting';
        default:
            return 'done';
    }
}

/**
 * Chunks grouped by status, errors first.
 *
 * The page is about failures: you open it to see whether a kørsel has stalled or failed, not to
 * confirm that the completed chunks completed. The order here plus the default expansion in
 * ExecutorAccordionComponent is what makes a failed kørsel show its errors without a click.
 *
 * Ordered by `current` within a group: stable, and the only order a non-admin can see. `startedAt`
 * exists for kørsler since 2026-09 and is shown next to the chunk for admins.
 */
export function groupExecutors(executers: readonly QueueExecuterDto[]): ExecutorGroup[] {
    return EXECUTOR_GROUP_ORDER
        .map(group => ({
            ...group,
            executers: executers
                .filter(executer => executorGroupOf(executer.status) === group.key)
                .sort((a, b) => a.current - b.current)
        }))
        .filter(group => group.executers.length > 0);
}

// ── Phase timelines (developer detail) ───────────────────────────────────────

/** A phase with how long it lasted: the gap to the next phase, or to `end` for the last one. */
export interface PhaseSpan {
    status: QStatus | null;
    start: string;
    /** Null when the phase is terminal (nothing follows it) or still running with no end in sight. */
    durationMs: number | null;
    /** Rendered duration, '' for a terminal phase, '…' for one still running. */
    durationLabel: string;
    terminal: boolean;
}

/**
 * Durations from consecutive tracker rows. A phase lasts until the next one starts; the last phase
 * lasts until `end` (the chunk's or kørsel's completion), or is open-ended when there is none yet.
 * A terminal status (Done, Failed …) is an instant, not a span, and gets no duration.
 */
export function phaseSpans(phases: readonly QueuePhaseDto[], end: string | null): PhaseSpan[] {
    return phases.map((phase, index) => {
        const terminal = index === phases.length - 1 && isExecutorSettled(phase.status);
        const next = phases[index + 1]?.start ?? end;
        const durationMs = terminal || !next ? null : durationBetween(phase.start, next);
        return {
            status: phase.status,
            start: phase.start,
            durationMs,
            durationLabel: terminal ? '' : durationMs === null ? '…' : formatDurationMs(durationMs),
            terminal
        };
    });
}

/** Milliseconds from `from` to `to`, or null when either does not parse. Never negative. */
export function durationBetween(from: string, to: string): number | null {
    const ms = Date.parse(to) - Date.parse(from);
    return Number.isFinite(ms) ? Math.max(0, ms) : null;
}

/**
 * Compact Danish duration: "420 ms", "6,1 s", "1 m 12 s", "1 t 03 m". One unit past the second,
 * because the point is comparing phases at a glance, not reading a clock.
 */
export function formatDurationMs(ms: number): string {
    if (ms < 1000) return `${Math.round(ms)} ms`;
    const seconds = ms / 1000;
    if (seconds < 60) return `${seconds.toFixed(1).replace('.', ',')} s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} m ${String(Math.round(seconds % 60)).padStart(2, '0')} s`;
    const hours = Math.floor(minutes / 60);
    return `${hours} t ${String(minutes % 60).padStart(2, '0')} m`;
}

// ── Time windows ─────────────────────────────────────────────────────────────

/** Inclusive range the day picker offers, in local time. */
export function dayPickerRange(now: Date = new Date()): { min: Date; max: Date } {
    const max = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const min = new Date(max);
    min.setDate(min.getDate() - (DAY_PICKER_DAYS - 1));
    return { min, max };
}

/** 'yyyy-MM-dd' in local time — deliberately not toISOString(), which would shift the date. */
export function toDayKey(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * The two UTC instants a local 'yyyy-MM-dd' actually spans.
 *
 * The server never guesses a timezone: "28 July" is a different pair of instants in Danish local
 * time than in UTC, so the client sends the boundaries it means.
 */
export function dayBounds(dayKey: string): { from: string; to: string } | null {
    const [year, month, day] = dayKey.split('-').map(Number);
    if (!year || !month || !day) return null;

    const from = new Date(year, month - 1, day);
    const to = new Date(year, month - 1, day + 1);
    return { from: from.toISOString(), to: to.toISOString() };
}

/** The narrowest rolling window that contains an instant, or null when nothing does. */
export function narrowestWindowFor(at: Date, now: Date = new Date()): QueueTimeWindow | null {
    const age = now.getTime() - at.getTime();
    if (age <= HOUR_MS) return '1h';
    if (age <= DAY_MS) return '24h';
    if (age <= WEEK_MS) return '7d';
    return null;
}

const WINDOW_SPAN: Record<Exclude<QueueTimeWindow, 'day'>, number> = {
    '1h': HOUR_MS,
    '24h': DAY_MS,
    '7d': WEEK_MS
};

/**
 * Whether `current` already covers everything `candidate` does.
 *
 * Used to make the deep-link hint widen-only: a URL that already asked for 7d must never be
 * narrowed to 1h just because the kørsel it points at happens to be recent. A picked single day is
 * treated as not covering anything, so it always yields to the hint.
 */
export function windowContains(current: QueueTimeWindow, candidate: QueueTimeWindow): boolean {
    if (current === 'day' || candidate === 'day') return false;
    return WINDOW_SPAN[current] >= WINDOW_SPAN[candidate];
}

// ── Payload download ─────────────────────────────────────────────────────────

/**
 * Filename for a downloaded payload: `tenantid-companyid-skabelon-task-datetime-n-m.json`.
 *
 * `customerId` is the tenant id — KundeId, CustomerId and TenantId are the same integer, so no
 * translation is needed to satisfy the "tenantid" part.
 *
 * The stamp is the kørsel's start in local time as `yyyyMMdd-HHmmss`: sortable, locale-independent,
 * and the same instant the header shows under "Startet". Everything up to it is shared by every
 * chunk of one kørsel; the trailing `n-m` is what separates them, so all the parts of a run land
 * next to each other in a folder listing.
 */
export function payloadFileName(detail: QueueRequestDetailDto, executer: QueueExecuterDto): string {
    return [
        detail.customerId,
        detail.companyId,
        slugify(detail.templateName) || 'uden-skabelon',
        // Task is the read/write/delete discriminator; action only distinguishes synchronize from
        // template. Both are free nvarchar, so neither is guaranteed to be present.
        slugify(detail.task) || slugify(detail.action) || 'ukendt',
        fileStamp(detail.created ?? detail.lastActivity),
        executer.current,
        executer.total
    ].join('-') + '.json';
}

/**
 * Down to letters, digits and single hyphens. Template names are free text and reach here verbatim —
 * a slash or a colon in one would otherwise produce a filename the OS rejects. Danish letters are
 * kept: they are valid in a filename everywhere this runs, and stripping them mangles the name.
 */
function slugify(value: string | null): string {
    return (value ?? '')
        .toLowerCase()
        .replace(/[^\p{Letter}\p{Number}]+/gu, '-')
        .replace(/^-+|-+$/g, '');
}

function fileStamp(iso: string): string {
    const at = new Date(iso);
    if (Number.isNaN(at.getTime())) return 'ukendt-tid';

    const pad = (part: number) => String(part).padStart(2, '0');
    return (
        `${at.getFullYear()}${pad(at.getMonth() + 1)}${pad(at.getDate())}` +
        `-${pad(at.getHours())}${pad(at.getMinutes())}${pad(at.getSeconds())}`
    );
}
